import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { AudienceTag } from "../components/Tags";

const PILLARS = [
  {
    n: "01",
    title: "Manufacturing & Engineering",
    tags: ["business"],
    body: "Factory-floor interpretation, technical specification translation, production-line communication protocols, and engineering collaboration for Japanese and Canadian manufacturing operations.",
    links: [["Language Services", "/services#language-services"], ["Consulting & Business Support", "/services#consulting-business-support"]],
  },
  {
    n: "02",
    title: "Medical & Healthcare",
    tags: ["business", "individual"],
    body: "Medical documentation translation, regulatory compliance navigation, clinical trial liaison, and healthcare communication support where precision can be life-critical.",
    links: [["Language Services", "/services#language-services"], ["Relocation & Navigation", "/services#relocation-navigation"]],
  },
  {
    n: "03",
    title: "Legal & Professional Services",
    tags: ["business", "individual"],
    body: "Legal document translation, contract negotiation support, cross-border advisory, and professional service delivery across legal, financial, and consulting domains.",
    links: [["Language Services", "/services#language-services"], ["Consulting & Business Support", "/services#consulting-business-support"]],
  },
  {
    n: "04",
    title: "Corporate / International Teams",
    tags: ["business"],
    body: "Cross-border team integration, internal communication infrastructure, executive support, and the bilingual operational backbone for organizations operating across Japan and Canada.",
    links: [["Consulting & Business Support", "/services#consulting-business-support"], ["Education & Academics", "/services#education-academics"]],
  },
  {
    n: "05",
    title: "Education / Academics",
    tags: ["business"],
    body: "Educational institution partnerships, academic exchange support, language program development, and cross-cultural training for educational organizations building bridges between Japan and Canada.",
    links: [["Education & Academics", "/services#education-academics"]],
  },
];

export default function Industries() {
  return (
    <div data-testid="industries-page" className="bg-slatebg pt-16">
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pt-20 sm:pt-28 pb-10">
        <Reveal>
          <Overline>INDUSTRIES</Overline>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight text-navy max-w-3xl">
            Five fields where the crossing is hardest — and where we're most at home.
          </h1>
          <p className="mt-8 text-base sm:text-lg leading-relaxed text-charcoal/75 max-w-2xl">
            Each pillar below links through to the service categories that carry the work.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pb-24">
        <div className="border-t border-slateborder">
          {PILLARS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.04}>
              <div data-testid={`industry-pillar-${p.n}`} className="border-b border-slateborder py-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-1">
                  <span className="font-mono text-sm tracking-widest text-coral-dark">{p.n}</span>
                </div>
                <div className="lg:col-span-4">
                  <h2 className="font-serif text-2xl sm:text-3xl text-navy">{p.title}</h2>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {p.tags.map((t) => (
                      <AudienceTag key={t} audience={t} testId={`industry-pillar-${p.n}-tag-${t}`} />
                    ))}
                  </div>
                </div>
                <div className="lg:col-span-5">
                  <p className="text-sm sm:text-base leading-relaxed text-charcoal/70">{p.body}</p>
                </div>
                <div className="lg:col-span-2">
                  <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-charcoal/50 mb-3">Relevant services</p>
                  <div className="flex flex-col gap-2">
                    {p.links.map(([label, to]) => (
                      <Link
                        key={label}
                        to={to}
                        data-testid={`industry-${p.n}-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="group inline-flex items-center gap-1.5 text-sm font-medium text-coral-dark hover:text-coral transition-colors duration-300"
                      >
                        {label}
                        <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection />
    </div>
  );
}
